'use client';

import { BigNum, SpotMarketConfig, WRAPPED_SOL_MINT } from '@drift-labs/sdk';
import { COMMON_UI_UTILS, MarketId } from '@drift/common';
import { PublicKey, SystemProgram } from '@solana/web3.js';
import {
	getAssociatedTokenAddress,
	createAssociatedTokenAccountInstruction,
	createCloseAccountInstruction,
} from '@solana/spl-token';
import { useCallback } from 'react';
import { ComputeUnits } from 'src/@types/types';
import captureException from 'src/utils/captureException';
import { notify } from 'src/utils/notifications';
import TransactionErrorHandler from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import { getOracleCrankIxs } from 'src/utils/oracle';
import { updateAfterTx } from 'src/stores/DriftStore/helpers';
import useDriftAccountsStore from 'src/stores/useDriftAccountsStore';
import useCurrentSlotRef from 'src/hooks/useCurrentSlotRef';
import { useGetTxParams } from 'src/hooks/driftActions/transactions/useGetTxParams';
import useDriftClient from '../../useDriftClient';
import invariant from 'tiny-invariant';
import useCurrentUserAccount from '../../useCurrentUserAccount';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { getTokenProgramForSpotMarket } from '@drift-labs/sdk';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useSponsoredSignDriftActionWithFallback } from 'src/hooks/wallets/useSponsoredSignDriftActionWithFallback';
import type { WalletName } from '@solana/wallet-adapter-base';
import { useQueryClient } from '@tanstack/react-query';
import { accountTradingDataQueryKeys } from 'src/hooks/query/accountTradingData/accountTradingDataQueryKeys';
import type { PositionsByUserKey } from 'src/hooks/query/accountTradingData/utils/types';

interface WithdrawToUdaParams {
	amount: BigNum;
	spotMarketConfig: SpotMarketConfig;
	/** The UDA Solana wallet address from Fun.xyz */
	udaSolanaAddress: string;
}

/**
 * Withdraw from Drift account directly to a Fun.xyz UDA (Universal Deposit Address).
 *
 * Uses getWithdrawIx directly (not getWithdrawalIxs) because we need to
 * create the UDA's ATA with the UDA as owner, not the user's wallet.
 *
 * For native SOL: withdraws wSOL to user's ATA, unwraps to native SOL,
 * then transfers native SOL to UDA (Fun.xyz expects native SOL, not wSOL).
 */
export function useWithdrawToUda() {
	const driftClient = useDriftClient();
	const currentUserAccount = useCurrentUserAccount();
	const walletAdapter = useDriftStore((s) => s.wallet.current.adapter);

	const accountsGet = useDriftAccountsStore((s) => s.get);
	const currentSlotRef = useCurrentSlotRef();
	const getTxParams = useGetTxParams();
	const queryClient = useQueryClient();

	const withdrawToUdaAction = useCallback(
		async (
			wallet: UiEnhancedIWalletV2,
			amount: BigNum,
			spotMarketConfig: SpotMarketConfig,
			udaSolanaAddress: string,
			walletPubkey: PublicKey,
			allActiveMarketIds: MarketId[],
			walletName: WalletName<string>,
			currentSlot: number
		) => {
			invariant(driftClient, 'driftClient is required');

			const spotMarketAccount = driftClient.getSpotMarketAccount(
				spotMarketConfig.marketIndex
			);
			if (!spotMarketAccount) throw new Error('Failed to get spot market');

			const optionalIxs = await getOracleCrankIxs(
				allActiveMarketIds,
				driftClient,
				walletName,
				{ current: currentSlot },
				undefined,
				2
			);

			const transferAmount = amount.shiftTo(spotMarketConfig.precisionExp);

			const udaPubkey = new PublicKey(udaSolanaAddress);
			const tokenProgram = getTokenProgramForSpotMarket(spotMarketAccount);
			const isNativeSol = spotMarketAccount.mint.equals(WRAPPED_SOL_MINT);

			const ixs = [];

			if (isNativeSol) {
				// Native SOL: withdraw wSOL to user's ATA, unwrap, then transfer native SOL to UDA
				const userWsolAta = await getAssociatedTokenAddress(
					WRAPPED_SOL_MINT,
					walletPubkey,
					false,
					tokenProgram
				);

				const userAtaExists =
					await driftClient.connection.getAccountInfo(userWsolAta);
				if (!userAtaExists) {
					ixs.push(
						createAssociatedTokenAccountInstruction(
							walletPubkey,
							userWsolAta,
							walletPubkey,
							WRAPPED_SOL_MINT,
							tokenProgram
						)
					);
				}

				// Withdraw wSOL from Drift to user's own ATA
				const withdrawIx = await driftClient.getWithdrawIx(
					transferAmount.val,
					spotMarketConfig.marketIndex,
					userWsolAta,
					true,
					undefined
				);
				ixs.push(withdrawIx);

				// Close user's wSOL ATA → unwraps all wSOL to native SOL in user's wallet
				ixs.push(
					createCloseAccountInstruction(
						userWsolAta,
						walletPubkey,
						walletPubkey,
						[],
						tokenProgram
					)
				);

				// Transfer native SOL to UDA
				ixs.push(
					SystemProgram.transfer({
						fromPubkey: walletPubkey,
						toPubkey: udaPubkey,
						lamports: transferAmount.val.toNumber(),
					})
				);
			} else {
				// SPL tokens: send directly to UDA's ATA
				const udaTokenAccount = await getAssociatedTokenAddress(
					spotMarketAccount.mint,
					udaPubkey,
					true, // allowOwnerOffCurve — UDA might be a PDA
					tokenProgram
				);

				const accountExists =
					await driftClient.connection.getAccountInfo(udaTokenAccount);
				if (!accountExists) {
					ixs.push(
						createAssociatedTokenAccountInstruction(
							walletPubkey,
							udaTokenAccount,
							udaPubkey,
							spotMarketAccount.mint,
							tokenProgram
						)
					);
				}

				const withdrawIx = await driftClient.getWithdrawIx(
					transferAmount.val,
					spotMarketConfig.marketIndex,
					udaTokenAccount,
					true,
					undefined
				);
				ixs.push(withdrawIx);
			}

			const txParams = getTxParams({
				marketId: MarketId.createSpotMarket(spotMarketConfig.marketIndex),
				computeUnits: 1_200_000 as ComputeUnits,
				lowerBoundCu: 450_000,
			});

			await wallet.signAndSendTransaction([...optionalIxs, ...ixs], txParams);
		},
		[driftClient, getTxParams]
	);

	const wrappedWithdrawToUda =
		useSponsoredSignDriftActionWithFallback(withdrawToUdaAction);

	const withdrawToUda = useCallback(
		async ({
			amount,
			spotMarketConfig,
			udaSolanaAddress,
		}: WithdrawToUdaParams) => {
			invariant(driftClient, 'Failed to get drift client');

			const userAccount = currentUserAccount?.client?.getUserAccount();
			const walletPubkey = walletAdapter?.publicKey;

			if (
				!driftClient ||
				!walletAdapter ||
				!walletPubkey ||
				!userAccount ||
				!userAccount.authority.equals(walletPubkey)
			)
				return;

			const toastId = `withdraw-to-uda-${amount.toString()}-${Date.now()}`;

			await driftClient.switchActiveUser(
				userAccount.subAccountId,
				userAccount.authority
			);

			const userKey = COMMON_UI_UTILS.getUserKey(
				userAccount.subAccountId,
				userAccount.authority
			);
			const accountData = accountsGet().accounts[userKey];

			const positionsData = queryClient.getQueryData<PositionsByUserKey>(
				accountTradingDataQueryKeys.positions()
			);
			const perpMarketIds = (
				positionsData?.[userKey]?.openPerpPositions ?? []
			).map((position) => MarketId.createPerpMarket(position.marketIndex));

			const spotMarketIds =
				accountData?.spotBalances.map((balance) =>
					MarketId.createSpotMarket(balance.asset.marketIndex)
				) ?? [];

			const allActiveMarketIds = [...perpMarketIds, ...spotMarketIds];

			const walletName = walletAdapter?.name;
			if (!walletName) return false;

			try {
				notify({
					type: 'awaiting_wallet_confirmation',
					id: toastId,
					showUntilCancelled: true,
				});

				await wrappedWithdrawToUda(
					amount,
					spotMarketConfig,
					udaSolanaAddress,
					walletPubkey,
					allActiveMarketIds,
					walletName,
					currentSlotRef.current
				);

				driftClient.spotMarketLastSlotCache.set(
					spotMarketConfig.marketIndex,
					currentSlotRef.current
				);

				notify({
					type: 'success',
					message: 'Withdrawal to external destination submitted.',
					id: toastId,
					updatePrevious: true,
				});

				const userClient = accountData?.client;
				if (userClient) {
					updateAfterTx(driftClient, userClient);
				}

				return true;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (e: any) {
				console.error(e);
				captureException(e);
				TransactionErrorHandler.handleError({
					error: e,
					toastId,
					wallet: walletAdapter,
				});
				return false;
			}
		},
		[
			driftClient,
			walletAdapter,
			currentUserAccount,
			accountsGet,
			wrappedWithdrawToUda,
			currentSlotRef,
			queryClient,
		]
	);

	return { withdrawToUda };
}
